import { NavTabId } from '../ui/components/Navbar.tsx';

export interface AppUrlState {
  tab: NavTabId;
  subtab?: string;
  editEventId?: string;
}

const VALID_TABS: NavTabId[] = ['calendar', 'analysis', 'finances'];

/**
 * Parses the current window.location.search parameters into a clean AppUrlState object.
 */
export function parseUrlState(): AppUrlState {
  if (typeof window === 'undefined') {
    return { tab: 'calendar' };
  }

  const params = new URLSearchParams(window.location.search);
  const rawTab = params.get('tab') as NavTabId;
  const tab: NavTabId = VALID_TABS.includes(rawTab) ? rawTab : 'calendar';
  const subtab = params.get('subtab') || undefined;
  const editEventId = params.get('edit') || params.get('eventId') || undefined;

  return {
    tab,
    subtab,
    editEventId,
  };
}

/**
 * Encodes the given state updates into URL search parameters and updates window.history.
 */
export function updateUrlState(
  updates: Partial<AppUrlState>,
  replace = true
): void {
  if (typeof window === 'undefined') return;

  const currentState = parseUrlState();
  const nextState: AppUrlState = {
    ...currentState,
    ...updates,
  };

  const params = new URLSearchParams();
  if (nextState.tab) {
    params.set('tab', nextState.tab);
  }
  if (nextState.subtab && nextState.tab === 'analysis') {
    params.set('subtab', nextState.subtab);
  }
  if (nextState.editEventId) {
    params.set('edit', nextState.editEventId);
  }

  const searchStr = params.toString();
  const newUrl = `${window.location.pathname}${searchStr ? `?${searchStr}` : ''}${window.location.hash}`;

  if (window.location.search !== `?${searchStr}` && window.location.href !== newUrl) {
    if (replace) {
      window.history.replaceState(null, '', newUrl);
    } else {
      window.history.pushState(null, '', newUrl);
    }
  }
}

/**
 * Listens for browser Back/Forward navigation (popstate) events.
 */
export function listenToUrlChanges(onStateChange: (state: AppUrlState) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const handlePopState = () => {
    onStateChange(parseUrlState());
  };

  window.addEventListener('popstate', handlePopState);
  return () => {
    window.removeEventListener('popstate', handlePopState);
  };
}
